import React from "react";
import { X } from "lucide-react";

/**
 * Reusable Modal with modern design - supports flexible form content
 * @param {Object} props
 * @param {boolean} props.isOpen - Modal visibility
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onSubmit - Submit handler
 * @param {string} props.title - Modal title
 * @param {React.ReactNode} props.children - Form content (can be single input or multiple fields)
 * @param {string} props.submitLabel - Submit button label (default: "Simpan")
 * @param {string} props.cancelLabel - Cancel button label (default: "Batal")
 *
 * BACKWARD COMPATIBILITY - supports old single-input props:
 * @param {string} props.value - Input value (for single input)
 * @param {Function} props.onChange - Input change handler (for single input)
 * @param {string} props.placeholder - Input placeholder (for single input)
 * @param {React.Ref} props.inputRef - Input ref for autofocus (for single input)
 */
export default function AddItemModal({
  isOpen,
  onClose,
  onSubmit,
  title,
  children,
  submitLabel = "Simpan",
  cancelLabel = "Batal",
  // Backward compatibility props
  value,
  onChange,
  placeholder,
  inputRef,
}) {
  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  // Check if using old single-input mode or new children mode
  const useSingleInput = !children && (value !== undefined || onChange);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 transform transition-all animate-slideUp">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Backward compatibility: render single input if old props are used */}
          {useSingleInput ? (
            <input
              ref={inputRef}
              type="text"
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm"
              autoComplete="off"
            />
          ) : (
            // New mode: render children for flexible form content
            children
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm"
            >
              {cancelLabel}
            </button>
            <button
              type="submit"
              className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm shadow-sm"
            >
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
